import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { T, Icon } from '../ProjectOverview/parts/stitch';
import { useProjectData } from '../ProjectOverview/ProjectDataContext';
import PhaseRail from './PhaseRail';
import StagePanel from './StagePanel';
import ClientPassportCard from './ClientPassportCard';
import PhaseSettingsDialog from './PhaseSettingsDialog';
import OpstartWizard from './OpstartWizard';
import GenResultDialog from './GenResultDialog';
import TeamsKanaalRegel from './TeamsKanaalRegel';
import VraagHetProject from './VraagHetProject';

/**
 * De projectflow: fasenbalk + werkvlak per fase + klantpaspoort. Dit is de
 * startpagina van /projects/:id; de oude tabbladen blijven bereikbaar via
 * ?weergave=tabs (ontsnappingsluik) en elk paneel opent als drawer via
 * ?paneel=<key> (onOpenPaneel — geen enkel tabblad wordt onbereikbaar).
 */
function ProjectFlowPage({ onOpenPaneel, onOpenDialog }) {
  const { id, project } = useProjectData();
  const [flow, setFlow] = useState(null);
  const [flowError, setFlowError] = useState(null);
  const [activeKey, setActiveKey] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [genResult, setGenResult] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // ?opstart=1 (deeplink vanaf het PL-dashboard) opent de wizard direct; de
  // parameter gaat er meteen weer af, zodat sluiten of verversen hem niet heropent.
  useEffect(() => {
    if (searchParams.get('opstart') !== '1') return;
    setWizardOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete('opstart');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const loadFlow = useCallback(async () => {
    try {
      setFlowError(null);
      const r = await authFetch(`${BACKEND_URL}/Api/project-flow/${id}`);
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
      setFlow(await r.json());
    } catch (e) { setFlowError(e.message); }
  }, [id]);

  useEffect(() => { if (id) loadFlow(); }, [id, loadFlow]);

  const phases = useMemo(() => flow?.phases || [], [flow]);
  const zichtbaar = useMemo(() => phases.filter((p) => p.is_enabled), [phases]);

  // Standaard-selectie: de laatst bekeken fase van dít project (onthouden),
  // anders de eerste fase die 'bezig' is, anders de eerste die nog niet klaar
  // is, anders de laatste.
  useEffect(() => {
    if (!zichtbaar.length) return;
    if (activeKey && zichtbaar.some((p) => p.phase_key === activeKey)) return;
    let onthouden = null;
    try { onthouden = localStorage.getItem(`flowFase:${id}`); } catch { /* prima */ }
    if (onthouden && zichtbaar.some((p) => p.phase_key === onthouden)) {
      setActiveKey(onthouden);
      return;
    }
    const bezig = zichtbaar.find((p) => p.status === 'bezig');
    const open = zichtbaar.find((p) => p.status !== 'klaar');
    setActiveKey((bezig || open || zichtbaar[zichtbaar.length - 1]).phase_key);
  }, [zichtbaar, activeKey, id]);

  const kiesFase = (key) => {
    setActiveKey(key);
    try { localStorage.setItem(`flowFase:${id}`, key); } catch { /* prima */ }
  };

  const schakelFaseUit = async (p) => {
    try {
      await authFetch(`${BACKEND_URL}/Api/project-flow/${id}/phases/${p.id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_enabled: false }),
      });
      loadFlow();
    } catch { /* fase blijft staan */ }
  };

  const actieveFase = zichtbaar.find((p) => p.phase_key === activeKey) || null;
  const clientNames = project?.fields?.KlantnaamString || project?.fields?.Klantnaam || [];
  const clientName = Array.isArray(clientNames) ? clientNames[0] : clientNames || '';

  if (flowError) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="body2" sx={{ fontFamily: T.fontBody, color: T.error, mb: 1 }}>
          Kon de projectflow niet laden: {flowError}
        </Typography>
        <Typography variant="body2" onClick={loadFlow}
          sx={{ fontFamily: T.fontBody, color: T.primary, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
          Opnieuw proberen
        </Typography>
      </Box>
    );
  }
  if (!flow) {
    return (
      <Box sx={{ py: 6, display: 'grid', placeItems: 'center' }}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <PhaseRail
            phases={phases}
            activeKey={activeKey}
            onSelect={kiesFase}
            onDisable={schakelFaseUit}
            onOpenSettings={() => setSettingsOpen(true)}
            driftCount={(flow.template_drift?.missing_phase_keys || []).length}
          />
        </Box>
        <Box component="span" onClick={() => setWizardOpen(true)} sx={{
          display: 'inline-flex', alignItems: 'center', gap: 0.6, px: 1.5, py: 1.1,
          borderRadius: '9999px', cursor: 'pointer', flexShrink: 0,
          border: `1.5px solid ${T.tertiary}`, color: T.tertiary,
          fontFamily: T.font, fontSize: '0.78rem', fontWeight: 800,
          '&:hover': { bgcolor: T.tertiaryContainer + '44' },
        }}>
          <Icon name="rocket_launch" size={16} />
          Project opstarten
        </Box>
      </Box>
      <TeamsKanaalRegel projectRecId={id} />
      <VraagHetProject projectRecId={id} />

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mt: 1.5,
        flexDirection: { xs: 'column', lg: 'row' } }}>
        <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
          <StagePanel phase={actieveFase} project={project} onOpenPaneel={onOpenPaneel} onOpenDialog={onOpenDialog}
            onFlowChanged={loadFlow} />
        </Box>
        {/* Onder lg klapt het paspoort boven-onder; op breed sticky rechts */}
        <Box sx={{ width: { xs: '100%', lg: 300 }, flexShrink: 0,
          position: { lg: 'sticky' }, top: 12, alignSelf: { lg: 'flex-start' } }}>
          <ClientPassportCard project={project} clientName={clientName} flow={flow} onFlowChanged={loadFlow} />
        </Box>
      </Box>

      <OpstartWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        project={project}
        onOpenDialog={onOpenDialog}
        onOpenFasen={() => setSettingsOpen(true)}
        onDebriefResultaat={setGenResult}
      />
      <GenResultDialog result={genResult} onClose={() => setGenResult(null)} projectRecId={id} />

      <PhaseSettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        projectRecId={id}
        phases={phases}
        driftKeys={flow.template_drift?.missing_phase_keys || []}
        onChanged={loadFlow}
      />
    </Box>
  );
}

export default ProjectFlowPage;
